import React, { Fragment, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Grid, IconButton, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import moment from 'moment/moment';
import { extendMoment } from 'moment-range';
import ReportIcon from '@mui/icons-material/Report';

import { getProductsAvailabilityState } from '../../../../../features/reservation/get_products_availability';
import { DeleteButton } from '../../../../common/button';
import { isEmptyArray, isNullOrUndefined } from '../../../../../core/utils/misc_utils';
import useNotification from '../../../../../core/hooks/use_notification';
import { deleteProductAvailabilityAction, deleteProductAvailabilityState } from '../../../../../features/reservation/delete_product_availability';
import { deleteProductAvailabilityNotificationData } from '../../../../../core/utils/notification_utils';
import { ApiRequestStatusEnum } from '../../../../../core/enums/common/api';
import { MISC_DATE_ISO_DATE_ONLY_FORMAT, MISC_DATE_POLISH_DATE_ONLY_FORMAT } from '../../../../../core/constants';
import UpdateProductQuantityLimitDialog from './form/update_product_quantity_limit_dialog';
import ImportProductAvailabilityFormDialog from './form/import_product_availability_form_dialog';
import ProductAvailabilityFormDialog from './form/product_availability_form_dialog';

const CalendarList = ({ from, to, refreshCalendar }) => {
    const dispatch = useDispatch();
    const { showNotification } = useNotification();

    const getProductsAvailability = useSelector(getProductsAvailabilityState);
    const deleteProductAvailability = useSelector(deleteProductAvailabilityState);

    const [activeActionId, setActiveActionId] = useState(null);

    const onDeleteProduct = (productId, date) => {
        const actionKey = `${ productId }-${ date }`;
        setActiveActionId(actionKey);

        dispatch(deleteProductAvailabilityAction({ productId, fromDate: date, toDate: date }))
            .then(response => {
                showNotification(deleteProductAvailabilityNotificationData(response));
                if (response?.meta?.requestStatus === ApiRequestStatusEnum.FULFILLED) {
                    refreshCalendar();
                }
                setActiveActionId(null);
            })
            .catch(() => setActiveActionId(null));
    };

    const displayDates = () => {
        const momentRange = extendMoment(moment);

        return Array.from(momentRange.range(from, to).by('day'))
            .map(date => date.format(MISC_DATE_ISO_DATE_ONLY_FORMAT))
            .map(date => (
                <TableRow key={ date } sx={ { backgroundColor: 'slate.50' } }>
                    <TableCell colSpan={ 7 } align="center">
                        <Box sx={ { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 } }>
                            <Typography variant="subtitle2" sx={ { fontWeight: 600 } }>
                                { moment(date).format(MISC_DATE_POLISH_DATE_ONLY_FORMAT) }
                            </Typography>
                            <ImportProductAvailabilityFormDialog
                                refreshCalendar={ refreshCalendar }
                                targetDate={ date }
                            />
                        </Box>
                    </TableCell>
                </TableRow>
            ));
    };

    return (
        <TableContainer component={ Paper } variant="outlined">
            <Table stickyHeader size="small" sx={ { tableLayout: 'fixed' } }>
                <TableHead>
                    <TableRow sx={ { '& th': { backgroundColor: 'slate.50', fontWeight: 600 } } }>
                        <TableCell sx={ { width: '5%' } } align="center">Lp</TableCell>
                        <TableCell sx={ { width: '35%' } }>Nazwa</TableCell>
                        <TableCell align="center" sx={ { width: '20%' } }>Dostawca</TableCell>
                        <TableCell align="center" sx={ { width: '10%' } }>Max godzina</TableCell>
                        <TableCell align="center" sx={ { width: '5%' } }>Limit</TableCell>
                        <TableCell align="center" sx={ { width: '5%' } }>Zarez.</TableCell>
                        <TableCell align="center" sx={ { width: '20%' } }></TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {
                        getProductsAvailability?.data?.map((productsAvailability) => {
                            const sortedProducts = productsAvailability?.products || [];

                            return (
                                <Fragment key={ productsAvailability?.date }>
                                    <TableRow key={ productsAvailability?.date } sx={ { backgroundColor: 'slate.50' } }>
                                        <TableCell colSpan={ 7 } align="center">
                                            <Box sx={ {
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: 1
                                            } }>
                                                <Typography variant="subtitle2" sx={ { fontWeight: 600 } }>
                                                    {
                                                        moment(productsAvailability?.date, MISC_DATE_ISO_DATE_ONLY_FORMAT)
                                                            .format(MISC_DATE_POLISH_DATE_ONLY_FORMAT)
                                                    }
                                                </Typography>
                                                <ProductAvailabilityFormDialog
                                                    displayIcon={ true }
                                                    refreshCalendar={ refreshCalendar }
                                                    initialDate={ productsAvailability?.date }
                                                />
                                                <ImportProductAvailabilityFormDialog
                                                    refreshCalendar={ refreshCalendar }
                                                    targetDate={ productsAvailability?.date }
                                                />
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                    {
                                        sortedProducts.map((product, i) => {
                                            const actionKey = `${ product?.productId }-${ productsAvailability?.date }`;

                                            return (
                                                <TableRow
                                                    key={ product?.productName + productsAvailability?.date }
                                                    hover
                                                >
                                                    <TableCell align="center">{ i + 1 }</TableCell>
                                                    <TableCell>
                                                        <Grid container alignItems="center" columnGap={ 2 }>
                                                            {
                                                                product?.missingReservationSegmentCapacities &&
                                                                <Grid item>
                                                                    <Tooltip
                                                                        title="Maksymalna liczba sztuk w segmentach nie została uzupełniona">
                                                                        <IconButton size="small">
                                                                            <ReportIcon color="error" fontSize="small"/>
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                </Grid>
                                                            }
                                                            <Grid item xs>
                                                                { product?.productName }
                                                            </Grid>
                                                        </Grid>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        { product?.foodPartners?.map(foodPartner => foodPartner?.shortName).join(', ') }
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        { product?.reservationMaximumHour }
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        {
                                                            isNullOrUndefined(product?.generalQuantityLimit)
                                                                ? '-'
                                                                : product?.generalQuantityLimit
                                                        }
                                                    </TableCell>
                                                    <TableCell align="center" sx={ { fontWeight: 600 } }>
                                                        {
                                                            isNullOrUndefined(product?.quantityReserved)
                                                                ? 0
                                                                : product?.quantityReserved
                                                        }
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Stack direction="row" justifyContent="flex-end"
                                                               alignItems="center" spacing={ 1 }>
                                                            <UpdateProductQuantityLimitDialog
                                                                product={ product }
                                                                date={ productsAvailability?.date }
                                                                refreshCalendar={ refreshCalendar }
                                                            />
                                                            <DeleteButton
                                                                onDeleteClick={ () => onDeleteProduct(product?.productId, productsAvailability?.date) }
                                                                isLoading={ deleteProductAvailability?.loading && activeActionId === actionKey }
                                                            />
                                                        </Stack>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })
                                    }
                                </Fragment>
                            );
                        })
                    }
                    {
                        isEmptyArray(getProductsAvailability?.data) && displayDates()
                    }
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default CalendarList;